import React from "react";
import _ from "lodash";
import moment from 'moment-timezone';
import Switch from '@material-ui/core/Switch';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import EmailBarcodeField from '../../Fields/EmailBarcodeField';
import FieldGroup from "../../Fields/FieldGroup";
import Field from "../../Fields/Field";
import InputTextField from "../../Fields/InputTextField";
import InputDateField from "../../Fields/InputDateField";
import InputSelectField from "../../Fields/InputSelectField";
import InputPhoneNumberField from "../../Fields/InputPhoneNumberField";
import StatusSummaryTable from "../../Fields/StatusSummaryTable";
import ParkerAccessDeviceTable from "../../Fields/ParkerAccessDeviceTable";
import ParkerVehicleTable from "../../Fields/ParkerVehicleTable";
import StatusSummaryDisplay from "../../Fields/StatusSummaryDisplay";
import ParkerStatusTable from "../../Fields/ParkerStatusTable";

import ActionButton from "../../Common/ActionButton";
import NoticeBlock from "../../Common/NoticeBlock";
import MessageList from "../../Common/MessageList";
import PageHeader from "../../Common/PageHeader";
import ProcessingIndicator from "../../Common/ProcessingIndicator";

import ParkerActionsPanel from "../../Actions/ParkerActionsPanel";

import Parker from "../../../Entities/Parker";
import AccessDevice from "../../../Entities/AccessDevice";
import Vehicle from "../../../Entities/Vehicle";

import {
  queryListRates,
  queryListParkers,
  queryUpdateParker,
  queryGetSubscription,
} from "../../../Queries";

import { getFacility } from '../../../Utils/facility';

import "./ParkerDetailPage.scss";

const parkerQueryFields = [
  "id",
  "rate_id",
  "subscription_id",
  "suspension_status",
  "termination_status",
  "access_system_status",
  "first_name",
  "last_name",
  "email",
  "address",
  "city",
  "state",
  "zip",
  "phone",
  "parking_reason",
  "group",
  "pool",
  "passback",
  "reserved_space",
  { nearest_termination: ["id", "start_date"] },
  {
    parker_status_history: [
      "id",
      "action",
      "start_date",
      "end_date",
      "notes",
      "created_at"
    ],
  },
  {
    payor: ["readable_id", "info_name"],
  },
  {
    rate: ["id", "name", "active", "billing_profile_id"],
  },
  {
    vehicles: ["id", "license", "make", "model", "state"],
  },
  {
    access_devices: [
      "type",
      "device_identifier",
      "device_status",
      "device_date_added",
      "device_date_active",
      "device_date_inactive",
      "device_inactive_reason",
  ]},
];

const parkersQueryFields = [{ parkers: parkerQueryFields }];

const rateQueryFields = [
  {
    rates: ["id", "name", "active"],
  },
];

const billingProfileQueryFields = [
  {
    billing_profile: ["id", "billing_timing", "billing_day"],
  },
];

class ParkerDetailPage extends React.Component {
  state = {
    initializationComplete: false,
    initializationErrors: [],
    parker: null,
    billingProfile: null,
    editMode: false,
    saveInProgress: false,
    saveErrors: [],
    showInactiveDevices: false,
    statusHistory: [],
    accessSystemProvider: null,
  };

  parkerId = null;
  facilityId = null;
  fieldConfiguration = {};
  editParkerStore = null;

  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);
    this.parkerId = urlSearchParams.get("parker");
    this.facilityId = urlSearchParams.get('facility');
  }

  async componentDidMount() {
    const initializationTasks = [];

    initializationTasks.push(async () => {
      try {
        const data = await Parker.getFieldAllowedValues([
          "status",
          "suspension_status",
          "termination_status",
          "state",
          "access_system_status",
          "parkerStatusHistoryAction",
        ]);
        this.fieldConfiguration = {
          ...this.fieldConfiguration,
          ...data,
        };

        // Format access system status enum
        const accessSystemStatusOptions = this.fieldConfiguration.access_system_status.options;
        this.fieldConfiguration.access_system_status.options = accessSystemStatusOptions.map((option) => ({
         value: option.value,
         label: option.label.replace(' ', ' - '), 
        }));
      } catch (errors) {
        throw [errors].flat();
      }
    });

    initializationTasks.push(async () => {
      try {
        const data = await AccessDevice.getFieldAllowedValues(["type", "status", "device_inactive_reason"]);
        this.fieldConfiguration = {
          ...this.fieldConfiguration,
          access_device: data,
        };
      } catch (errors) {
        throw [errors].flat();
      }
    });

    initializationTasks.push(async () => {
      try {
        const data = await Vehicle.getFieldAllowedValues(["state"]);
        this.fieldConfiguration = {
          ...this.fieldConfiguration,
          vehicle: data,
        };
      } catch (errors) {
        throw [errors].flat();
      }
    });

    initializationTasks.push(async () => {
      try {
        const parker = await this.fetchParker();
        const { rate: parkerRate } = parker;

        const rates = await this.fetchRates(parkerRate.billing_profile_id);

        const billingProfile = await this.fetchBillingProfile(
          parker.subscription_id
        );

        const options = rates.map((rate) => ({
          value: rate.id,
          label: rate.name,
          disabled: rate.active === false,
        }));

        this.fieldConfiguration = {
          ...this.fieldConfiguration,
          rate: { options },
        };

        const parkerEntity = Parker.createFromQueryValues(parker);
        const statusHistory = this.getStatusHistory(
          parker.parker_status_history || []
        );

        this.setState({ parker: parkerEntity, statusHistory, billingProfile });
      } catch (errors) {
        throw [errors].flat();
      }
    });

    initializationTasks.push(async () => {
      const facility = await getFacility(this.facilityId);
      this.fieldConfiguration.facilityTimezone = facility.fields.timezone.value;
      this.setState({
        accessSystemProvider: facility.fields.accessSystem.value.provider || null
      })
    });

    try {
      const tasks = initializationTasks.map((task) => task());
      await Promise.all(tasks);

      this.setState({
        initializationComplete: true,
      });
    } catch (errors) {
      this.setState({
        initializationComplete: true,
        initializationErrors: errors,
      });
    }
  }

  async fetchParker(parkerId) {
    const filters = [
      {
        model: "PARKER",
        field: "id",
        op: "EQ",
        value: this.parkerId || parkerId,
      },
    ];

    try {
      const response = await queryListParkers({ filters }, parkersQueryFields);
      if (response.errors) {
        throw response.errors.map((error) => error.message);
      }

      const [parker] = response.data.listParkers.parkers;
      return parker;
    } catch (errors) {
      throw [errors].flat();
    }
  }

  fetchBillingProfile(subscriptionId) {
    return new Promise((resolve, reject) => {
      queryGetSubscription(
        {
          subscription_id: subscriptionId,
        },
        billingProfileQueryFields
      ).then(
        (response) => {
          if (response.errors) {
            reject(response.errors.map((error) => error.message));
            return;
          }

          const billingProfile = response.data.getSubscription.billing_profile;

          resolve(billingProfile);
        },
        (error) => {
          reject([error]);
        }
      );
    });
  }

  async fetchRates(billingProfileId) {
    const filters = [
      {
        model: "RATE",
        field: "billing_profile_id",
        op: "EQ",
        value: billingProfileId,
      },
    ];

    try {
      const response = await queryListRates({ filters }, rateQueryFields);
      if (response.errors) {
        throw response.errors.map((error) => error.message);
      }

      const { rates } = response.data.listRates;
      return rates;
    } catch (errors) {
      throw [errors].flat();
    }
  }

  async saveParker() {
    const { parker } = this.state;
    const parkerValues = parker.getQueryFieldValues();

    const options = {
      subscription_id: parker.fields.subscription_id.value,
      parker: parkerValues,
      start_date: this.getParkerStartDate(
        parker.fields.parker_status_history.value,
        "PARKER_START_DATE"
      ),
      access_system_start_date: this.getParkerStartDate(
        parker.fields.parker_status_history.value,
        "ACCESS_SYSTEM_START_DATE"
      ),
    };

    try {
      const response = await queryUpdateParker(options, parkerQueryFields);
      if (response.errors) {
        throw response.errors.map((error) => error.message);
      }
    } catch (errors) {
      throw [errors].flat();
    }
  }

  _onInputValueChange(inputId) {
    return (value) => {
      const parker = _.cloneDeep(this.state.parker);
      parker.fields[inputId].value = value;

      this.setState({
        parker,
      });
    };
  }

  onEditClick = () => {
    const { parker } = this.state;

    this.editParkerStore = _.cloneDeep(parker);

    this.setState({
      editMode: true,
    });
  };

  onSaveClick = () => {
    this.setState(
      {
        saveInProgress: true,
      },
      async () => {
        try {
          await this.saveParker();
          await this.refreshParker();

          this.editParkerStore = null;

          this.setState({
            saveInProgress: false,
            editMode: false,
            saveErrors: [],
          });
        } catch (errors) {
          this.setState({
            saveInProgress: false,
            saveErrors: errors,
          });
        }
      }
    );
  };

  onCancelClick = () => {
    const parker = this.editParkerStore;
    this.editParkerStore = null;

    this.setState({
      editMode: false,
      parker,
      saveErrors: [],

    });
  };

  parkerActionCallback = ({
    suspension_status,
    termination_status,
    access_system_status,
    rate_id,
  }) => {
    const parker = _.cloneDeep(this.state.parker);
    parker.fields.suspension_status.value = suspension_status;
    parker.fields.termination_status.value = termination_status;
    parker.fields.access_system_status.value = access_system_status;
    parker.fields.rate_id.value = rate_id;
    this.setState({
      parker,
    });
  };

  onEntryChange = (ident, value) => {
    let statusHistory = _.cloneDeep(this.state.statusHistory);
    statusHistory[ident] = value;
    this.setState({ statusHistory });
  };

  onEdit = (ident) => {
    let statusHistory = _.cloneDeep(this.state.statusHistory);
    statusHistory = statusHistory.map((i, index) =>
      ident === index ? i : { ...i, isEditable: false }
    );
    this.setState({ statusHistory });
  };

  onEditDone = () => {
    let statusHistory = _.cloneDeep(this.state.statusHistory);
    statusHistory = statusHistory.map((i) => ({ ...i, isEditable: true }));
    this.setState({ statusHistory });
  };

  getStatusHistory = (data) => {
    const statusHistory =
      data?.map((i) => ({
        ...i,
        noEndDate: i.end_date ? false : true,
        isEditable: true,
        terminationDate: i.action === "TERMINATE" ? i.start_date : null,
      })) || [];
    return statusHistory;
  };

  refreshParker = async (id) => {
    const parker = await this.fetchParker(id);
    const parkerEntity = Parker.createFromQueryValues(parker);
    this.setState({
      parker: parkerEntity,
      statusHistory: this.getStatusHistory(parker.parker_status_history || []),
    });
  };

  handleSwitchControl = (e) => {
    this.setState({
      [e.target.name]: e.target.checked
    })
  };

  renderPageHeader() {
    const { parker } = this.state;

    let heading = "Parker Detail";
    let subheading = null;

    if (parker !== null) {
      const parkerName = `${parker.fields.first_name.value} ${parker.fields.last_name.value}`;
      const payorId = parker.fields.payor.value.fields.readable_id.value;
      const billingName = parker.fields.payor.value.fields.info_name.value;

      const subheadingLine01 = `Billing Name: ${billingName}`;
      const subheadingLine02 = `Sub ID: ${payorId}`;

      heading = parkerName;
      subheading = [subheadingLine01, subheadingLine02];
    }

    return (
      <>
        <div className="parkchirp--grid-column parkchirp--grid-column-size--sidebar">
          {this.renderSidebar()}
        </div>
        <PageHeader heading={heading} subheading={subheading} />
      </>
    )
  }

  renderInitializationBlock() {
    const { initializationComplete, initializationErrors } = this.state;

    return (
      <>
        {!initializationComplete && (
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Initializing, please wait..." />
          </div>
        )}
        {initializationComplete && initializationErrors.length > 0 && (
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>
                The following errors occurred while initializing the Parkers
                panel:
              </p>
              <ul>
                {initializationErrors.map((error) => (
                  <li key={performance.now()}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </>
    );
  }

  renderSidebar() {
    const { editMode, saveInProgress, saveErrors } = this.state;

    return (
      <div className="parkchirp--sidebar-block">
        {editMode && (
          <StatusSummaryDisplay parker={this.state.parker} />
        )}

        {!editMode && (
          <ActionButton
            label="Edit"
            onClick={this.onEditClick}
          />
        )}
        {editMode && (
          <div>
            <ActionButton
              label="Save"
              inProgressState={saveInProgress}
              onClick={this.onSaveClick}
            />
            <ActionButton
              label="Cancel"
              inProgressState={false}
              onClick={this.onCancelClick}
            />
          </div>
        )}
        {!saveInProgress && saveErrors.length > 0 && (
          <NoticeBlock
            type="error"
          >
            <MessageList
              heading="The following errors occurred while attempting to save the Parker: "
              messages={saveErrors}
            />
          </NoticeBlock>
        )}
      </div>
    );
  }

  setParkerStartDate = (parker, action) => {
    return date => {
      const parkerStatusHistory = parker.fields.parker_status_history.value || [];
      const startDateRecordIndex = parkerStatusHistory.findIndex(record => record.action === action);

      if (startDateRecordIndex >= 0) {
        parkerStatusHistory[startDateRecordIndex].start_date = moment(date).tz('GMT').format('YYYY-MM-DD');
      } else {
        const startDateRecord = {
          action,
          start_date: moment(date).tz('GMT').format('YYYY-MM-DD'),
        };

        parkerStatusHistory.push(startDateRecord);
      }

      this._onInputValueChange('parker_status_history')(parkerStatusHistory);
    }
  }

  getParkerStartDate = (parkerStatusHistory, historyAction) => {
    const parkerStartDateRecord = parkerStatusHistory?.find(({ action }) => action === historyAction);
    return parkerStartDateRecord?.start_date ?? null;
  }

  getFormattedParkerStartDate = (parkerStatusHistory, action) => {
    const startDate = this.getParkerStartDate(parkerStatusHistory, action);

    // Stops page from crashing if there's an invalid date in our db
    // This should help with old records created before we added date validation
    if(Number.isNaN(Date.parse(startDate))) {
      return null;
    }

    return startDate ? new Date(startDate) : null;
  }

  renderParkerContent() {
    const { parker, editMode, showInactiveDevices } = this.state;

    const rates = this.fieldConfiguration.rate.options;
    const subscriptionID = parker.fields.subscription_id.value;
    const facilityParam = this.facilityId ? `&facility=${this.facilityId}` : "";
    const subscriptionParkerRosterLink = `/wp-admin/admin.php?page=parkchirp_subscription&subscription=${subscriptionID}${facilityParam}#/parkers`;
    const nearestTermination =
      parker.fields.nearest_termination.value?.start_date;
    const terminationDate = nearestTermination
      ? new Date(nearestTermination)
      : null;
    const parkerStartDate = this.getFormattedParkerStartDate(
      parker.fields.parker_status_history.value,
      "PARKER_START_DATE"
    );
    const accessSystemStartDate = this.getFormattedParkerStartDate(
      parker.fields.parker_status_history.value,
      "ACCESS_SYSTEM_START_DATE"
    );

    const accessDevices = parker.fields.access_devices.value;
    const barcodeDevice = accessDevices.find((device) => device.fields.type.value === 'BARCODE');
    const barcode = barcodeDevice?.fields.device_identifier.value;
    const parkerHasBarcode = !!barcode;
    
    return (
      <>
        <FieldGroup>
          <div className="parkchirp--parker-detail--first-block">
            <div className="parkchirp--parker-detail--navigation-row">
              <a href={subscriptionParkerRosterLink}>
                <button type="button" className="button button-primary">
                  Subscription Parker Roster
                </button>
              </a>
            </div>
            <div className="parkchirp--parker-detail--data-row">
              {editMode && (
                <>
                  <Field
                    label="First Name"
                    id="parkchirp--parker-detail--first-name"
                    layout="stacked"
                  >
                    <InputTextField
                      inputId="parkchirp--parker--first-name"
                      value={parker.fields.first_name.value}
                      onChange={this._onInputValueChange("first_name")}
                      editMode={editMode}
                    />
                  </Field>
                  <Field
                    label="Last Name"
                    id="parkchirp--parker-detail--last-name"
                    layout="stacked"
                  >
                    <InputTextField
                      inputId="parkchirp--parker--last-name"
                      value={parker.fields.last_name.value}
                      onChange={this._onInputValueChange("last_name")}
                      editMode={editMode}
                    />
                  </Field>
                </>
              )}
              {!editMode && (
                <>
                  <Field
                    label="Name"
                    id="parkchirp--parker-detail--name"
                    layout="stacked"
                  >
                    <p>
                      {parker.fields.first_name.value}
                      {<span> </span>}
                      {parker.fields.last_name.value}
                    </p>
                  </Field>
                  <>
                    <Field
                      label="Suspension Status"
                      id="parkchirp--parker-detail--suspension-status"
                      layout="stacked"
                    >
                      <InputSelectField
                        inputId="parkchirp--parker--suspension-status"
                        options={[
                          { value: null, label: "" },
                          ...this.fieldConfiguration.suspension_status.options,
                        ]}
                        value={
                          parker.fields.suspension_status.value
                            ? parker.fields.suspension_status.value
                            : "ACTIVE"
                        }
                        onChange={this._onInputValueChange("suspension_status")}
                        editMode={false}
                      />
                    </Field>
                    <Field
                      label="Termination Status"
                      id="parkchirp--parker-detail--termination-status"
                      layout="stacked"
                    >
                      <InputSelectField
                        inputId="parkchirp--parker--termination-status"
                        options={[
                          { value: null, label: "" },
                          ...this.fieldConfiguration.termination_status.options,
                        ]}
                        value={
                          parker.fields.termination_status.value
                            ? parker.fields.termination_status.value
                            : "ACTIVE"
                        }
                        onChange={this._onInputValueChange(
                          "termination_status"
                        )}
                        editMode={false}
                      />
                    </Field>
                  </>
                  <Field
                    label="Access System Status"
                    id="parkchirp--parker-detail--access-system-status"
                    layout="stacked"
                  >
                    <InputSelectField
                      inputId="parkchirp--parker--access-system-status"
                      options={[
                        { value: null, label: "" },
                        ...this.fieldConfiguration.access_system_status.options,
                      ]}
                      value={parker.fields.access_system_status.value}
                      onChange={this._onInputValueChange(
                        "access_system_status"
                      )}
                      editMode={false}
                    />
                  </Field>
                </>
              )}
            </div>
            {editMode && (
              <div className="parkchirp--parker-detail--data-row">
                <>
                  <Field
                    label="Address"
                    id="parkchirp--parker-detail--address"
                    layout="stacked"
                  >
                    <InputTextField
                      inputId="parkchirp--parker--address"
                      value={parker.fields.address.value}
                      onChange={this._onInputValueChange("address")}
                      editMode={editMode}
                    />
                  </Field>
                  <Field
                    label="City"
                    id="parkchirp--parker-detail--city"
                    layout="stacked"
                  >
                    <InputTextField
                      inputId="parkchirp--parker--city"
                      value={parker.fields.city.value}
                      onChange={this._onInputValueChange("city")}
                      editMode={editMode}
                    />
                  </Field>
                  <Field
                    label="State"
                    id="parkchirp--parker-detail--state"
                    layout="stacked"
                  >
                    <InputSelectField
                      inputId="parkchirp--parker--state"
                      options={[
                        { value: null, label: "" },
                        ...this.fieldConfiguration.state.options,
                      ]}
                      value={parker.fields.state.value}
                      onChange={this._onInputValueChange("state")}
                      editMode={editMode}
                    />
                  </Field>
                  <Field
                    label="ZIP"
                    id="parkchirp--parker-detail--zip"
                    layout="stacked"
                  >
                    <InputTextField
                      inputId="parkchirp--parker--zip"
                      value={parker.fields.zip.value}
                      onChange={this._onInputValueChange("zip")}
                      editMode={editMode}
                    />
                  </Field>
                </>
              </div>
            )}
            {!editMode && (
              <div className="parkchirp--parker-detail--data-row">
                <Field
                  label="Address"
                  id="parkchirp--parker-detail-address"
                  layout="stacked"
                >
                  <p>{parker.fields.address.value}</p>
                  <p>
                    {parker.fields.city.value}
                    {parker.fields.city.value !== null && <span>, </span>}
                    {parker.fields.state.value}
                    {parker.fields.state.value !== null && <span> </span>}
                    {parker.fields.zip.value}
                  </p>
                </Field>
              </div>
            )}
            <div className="parkchirp--parker-detail--data-row">
              <Field
                label="Phone"
                id="parkchirp--parker-detail--phone"
                layout="stacked"
              >
                <InputPhoneNumberField
                  inputId="parkchirp--parker--phone"
                  value={parker.fields.phone.value}
                  onChange={this._onInputValueChange("phone")}
                  editMode={editMode}
                />
              </Field>
              <Field
                label="Email"
                id="parkchirp--parker-detail--email"
                layout="stacked"
              >
                <InputTextField
                  inputId="parkchirp--parker--email"
                  value={parker.fields.email.value}
                  onChange={this._onInputValueChange("email")}
                  editMode={editMode}
                />
              </Field>
            </div>
            <div className="parkchirp--parker-detail--data-row">
              <Field
                label="Group"
                id="parkchirp--parker-detail--group"
                layout="stacked"
              >
                <InputTextField
                  inputId="parkchirp--parker--group"
                  value={parker.fields.group.value}
                  onChange={this._onInputValueChange("group")}
                  editMode={editMode}
                />
              </Field>
              <Field
                label="Pool"
                id="parkchirp--parker-detail--pool"
                layout="stacked"
              >
                <InputTextField
                  inputId="parkchirp--parker--pool"
                  value={parker.fields.pool.value}
                  onChange={this._onInputValueChange("pool")}
                  editMode={editMode}
                />
              </Field>
              <Field
                label="Passback"
                id="parkchirp--parker-detail--passback"
                layout="stacked"
              >
                <InputSelectField inputId="parker-passback"
                    options={[
                      { value: null, label: '' },
                      { value: true, label: 'On' },
                      { value: false, label: 'Off' }
                    ]}
                    value={parker.fields.passback.value}
                    onChange={this._onInputValueChange('passback')}
                    editMode={editMode}
                />
              </Field>
              <Field
                label="Reserved Space"
                id="parkchirp--parker-detail--reserved-space"
                layout="stacked"
              >
                <InputTextField
                  inputId="parkchirp--parker--reserved-space"
                  value={parker.fields.reserved_space.value}
                  onChange={this._onInputValueChange("reserved_space")}
                  editMode={editMode}
                />
              </Field>
            </div>
          </div>
        </FieldGroup>
        <FieldGroup label="Product Details">
          <div className="parkchirp--parker-detail--data-row">
            <Field
              label="Rate"
              id="parkchirp--parker-detail--rate"
              layout="columns"
            >
              <InputSelectField
                inputId="parkchirp--parker--rate"
                options={[
                  { value: null, label: "" },
                  ...this.fieldConfiguration.rate.options,
                ]}
                value={parker.fields.rate_id.value}
                onChange={this._onInputValueChange("rate")}
                editMode={false}
              />
            </Field>
          </div>
          <div className="parkchirp--parker-detail--data-row">
            <Field
              label="Payment Start Date"
              id="parkchirp--parker-detail--start-date"
              layout="columns"
            >
              <InputDateField
                inputId="parkchirp--parker--start-date"
                value={parkerStartDate}
                onChange={this.setParkerStartDate(parker, "PARKER_START_DATE")}
                editMode={editMode}
              />
            </Field>
            <Field
              label="Access System Start Date"
              id="parkchirp--parker-detail--access-system-start-date"
              layout="columns"
            >
              <InputDateField
                inputId="parkchirp--parker--access-system-start-date"
                value={accessSystemStartDate}
                onChange={this.setParkerStartDate(parker, "ACCESS_SYSTEM_START_DATE")}
                editMode={editMode}
              />
            </Field>
            <Field
              label="Termination Date"
              id="parkchirp--parker-detail--termination-date"
              layout="columns"
            >
              <InputDateField
                inputId="parkchirp--parker--termination-date"
                value={terminationDate}
                onChange={this._onInputValueChange("termination_date")}
                editMode={false}
              />
            </Field>
          </div>
        </FieldGroup>
        <FieldGroup label="Access Devices">
          <FormGroup row>
            <FormControlLabel
              control={
                <Switch
                  checked={showInactiveDevices}
                  color="primary"
                  name="showInactiveDevices"
                  onChange={this.handleSwitchControl}
                />
              }
              label="Show Inactive Devices"
            />
          </FormGroup>
          {this.state.accessSystemProvider === 'tiba' ? (
            <p>Tiba only supports a maximum of 5 access devices per parker.</p>
          ) : null}
          <ParkerAccessDeviceTable
            accessDevices={accessDevices}
            fieldConfiguration={
              Object.assign(
                this.fieldConfiguration.access_device,
                { facilityTimezone: this.fieldConfiguration.facilityTimezone }
              )
            }
            allowAdd={editMode}
            allowRemove={editMode}
            editMode={editMode}
            onChange={this._onInputValueChange("access_devices")}
            showInactiveDevices={this.state.showInactiveDevices}
          />
          {!editMode && parkerHasBarcode &&
            <EmailBarcodeField
              parkerId={this.parkerId}
              parkerEmail={parker.fields.email.value}
            />
          }
        </FieldGroup>
        <FieldGroup label="Vehicles">
          {this.state.accessSystemProvider === 'tiba' ? (
            <p>Tiba only supports a maximum of 5 vehicles per parker.</p>
          ) : null}
          <ParkerVehicleTable
            vehicles={parker.fields.vehicles.value}
            fieldConfiguration={this.fieldConfiguration.vehicle}
            allowAdd={editMode}
            allowRemove={editMode}
            editMode={editMode}
            onChange={this._onInputValueChange("vehicles")}
          />
        </FieldGroup>
        {!editMode && (
          <div classname="fieldgroup-group">
            <FieldGroup label="Actions">
              <ParkerActionsPanel
                parker={parker}
                rates={rates}
                parkerActionCallback={this.parkerActionCallback}
              />
            </FieldGroup>
          </div>
        )}
      </>
    );
  }

  renderParkerStatusTable() {
    const { parker, billingProfile } = this.state;
    return (
      <FieldGroup label="Parker Status">
        <StatusSummaryTable parker={parker} />
        <ParkerStatusTable
          fetchParker={this.refreshParker}
          parker={parker.fields}
          statusHistory={this.state.statusHistory}
          fieldConfiguration={this.fieldConfiguration}
          billingProfile={billingProfile}
          onEntryChange={this.onEntryChange}
          onEdit={this.onEdit}
          onEditDone={this.onEditDone}
          onCancelClick={() =>
            this.setState({
              statusHistory: this.getStatusHistory(
                parker.fields.parker_status_history.value || []
              ),
            })
          }
        />
      </FieldGroup>
    );
  }

  renderMainContent() {
    return (
      <>
        <div className="parkchirp--grid">
          <div className="parkchirp--grid-column parkchirp--grid-column-size--main-content">
            {this.renderParkerContent()}
          </div>
        </div>
        <div className="parker-status--table">
          {this.renderParkerStatusTable()}
        </div>
      </>
    );
  }

  render() {
    const { initializationComplete, initializationErrors } = this.state;

    return (
      <div className="parkchirp--page--parker-detail">
        {this.renderPageHeader()}
        {(!initializationComplete || initializationErrors.length > 0) &&
          this.renderInitializationBlock()}
        {initializationComplete &&
          initializationErrors.length === 0 &&
          this.renderMainContent()}
      </div>
    );
  }
}

export default ParkerDetailPage;
